import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { MdAdd, MdSearch, MdVisibility, MdAttachMoney } from 'react-icons/md';
import api from '../services/api';
import { SkeletonTableRows, EmptyState } from '../components/SkeletonLoader';

const QuotesContainer = styled.div`
  max-width: 1400px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${p => p.theme.spacing.xl};
  gap: ${p => p.theme.spacing.md};
  flex-wrap: wrap;
`;

const Title = styled.h1`
  font-size: ${p => p.theme.fontSizes.xxxl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0;

  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    font-size: ${p => p.theme.fontSizes.xxl};
  }
`;

const Button = styled.button`
  padding: 10px ${p => p.theme.spacing.lg};
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white;
  border: none;
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};
  box-shadow: 0 4px 14px ${p => p.theme.colors.primary}40;

  svg { font-size: 1.2rem; }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px ${p => p.theme.colors.primary}50;
  }
`;

const FilterSection = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.xl};
  box-shadow: ${p => p.theme.shadows.card};
`;

const SearchContainer = styled.div`
  position: relative;
  max-width: 500px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${p => p.theme.spacing.md};
  padding-left: 2.5rem;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background-color: ${p => p.theme.colors.background};
  transition: ${p => p.theme.transitions.fast};

  &:focus {
    border-color: ${p => p.theme.colors.primary};
    background-color: ${p => p.theme.colors.backgroundLight};
    outline: none;
  }

  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;

const SearchIcon = styled(MdSearch)`
  position: absolute;
  left: ${p => p.theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  color: ${p => p.theme.colors.textLight};
  font-size: 1.25rem;
`;

const FilterButtons = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.md};
  margin-top: ${p => p.theme.spacing.md};
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: 7px 18px;
  background: ${p => p.active
    ? `linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})`
    : p.theme.colors.backgroundDark};
  color: ${p => p.active ? 'white' : p.theme.colors.textSecondary};
  border: none;
  border-radius: 99px;
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: ${p => p.active ? 600 : 500};
  font-family: ${p => p.theme.fonts.primary};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};
  box-shadow: ${p => p.active ? `0 4px 12px ${p.theme.colors.primary}35` : 'none'};

  &:hover {
    background: ${p => p.active
      ? `linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})`
      : p.theme.colors.border};
    color: ${p => p.active ? 'white' : p.theme.colors.textPrimary};
  }
`;

const ListSection = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  box-shadow: ${p => p.theme.shadows.card};
`;

const SectionTitle = styled.h2`
  font-size: ${p => p.theme.fontSizes.xl};
  color: ${p => p.theme.colors.textPrimary};
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 720px;
`;

const Th = styled.th`
  padding: 8px ${p => p.theme.spacing.md};
  text-align: left;
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: 700;
  color: ${p => p.theme.colors.textLight};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const Td = styled.td`
  padding: ${p => p.theme.spacing.md};
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textPrimary};
  border-bottom: 1px solid ${p => p.theme.colors.borderLight};
`;

const StatusBadge = styled.span`
  padding: ${p => p.theme.spacing.xs} ${p => p.theme.spacing.sm};
  border-radius: ${p => p.theme.borderRadius.sm};
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: ${p => p.theme.fontWeights.medium};
  background-color: ${p => {
    const s = p.status?.toLowerCase();
    if (s === 'borrador') return p.theme.colors.textSecondary + '20';
    if (s === 'enviada') return p.theme.colors.primary + '20';
    if (s === 'aprobada') return p.theme.colors.success + '20';
    if (s === 'rechazada') return p.theme.colors.danger + '20';
    return p.theme.colors.textSecondary + '20';
  }};
  color: ${p => {
    const s = p.status?.toLowerCase();
    if (s === 'borrador') return p.theme.colors.textSecondary;
    if (s === 'enviada') return p.theme.colors.primary;
    if (s === 'aprobada') return p.theme.colors.success;
    if (s === 'rechazada') return p.theme.colors.danger;
    return p.theme.colors.textSecondary;
  }};
`;

const ActionButton = styled.button`
  padding: 5px 14px;
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white;
  border: none;
  border-radius: 8px;
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};

  svg { font-size: 1rem; }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 10px ${p => p.theme.colors.primary}40;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${p => p.theme.spacing.md};
  margin-top: ${p => p.theme.spacing.xl};
  flex-wrap: wrap;
`;

const PageButton = styled.button`
  padding: 6px 14px;
  background: ${p => p.active
    ? `linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})`
    : p.theme.colors.backgroundDark};
  color: ${p => p.active ? 'white' : p.theme.colors.textSecondary};
  border: none;
  border-radius: 8px;
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: ${p => p.active ? 600 : 400};
  font-family: ${p => p.theme.fonts.primary};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${p => p.active
      ? `linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})`
      : p.theme.colors.border};
  }

  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const FILTERS = ['Todos', 'Borrador', 'Enviada', 'Aprobada', 'Rechazada'];
const PER_PAGE = 10;

const Quotes = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('Todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => { loadQuotes(); }, []);
  useEffect(() => { setCurrentPage(1); }, [filter, searchTerm]);

  const loadQuotes = async () => {
    try {
      setLoading(true);
      const res = await api.get('/cotizaciones');
      setQuotes(res.data.data || res.data || []);
      setError(null);
    } catch (err) {
      setError(err.message || 'Error al cargar cotizaciones');
    } finally {
      setLoading(false);
    }
  };

  const filtered = quotes.filter(q => {
    const matchFilter = filter === 'Todos' || q.estado?.toLowerCase() === filter.toLowerCase();
    const s = searchTerm.toLowerCase();
    const matchSearch = !s ||
      q.numero_cotizacion?.toLowerCase().includes(s) ||
      q.cliente_nombre?.toLowerCase().includes(s);
    return matchFilter && matchSearch;
  });

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      pages.push(1, 2, 3, 4, '...', totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
    }
    return pages;
  };

  const countByStatus = s => quotes.filter(q => q.estado?.toLowerCase() === s.toLowerCase()).length;

  return (
    <QuotesContainer>
      <PageHeader>
        <Title>Cotizaciones</Title>
        <Button onClick={() => navigate('/cotizaciones/nueva')}>
          <MdAdd />
          Nueva Cotizacion
        </Button>
      </PageHeader>

      <FilterSection>
        <SearchContainer>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder="Buscar por numero de cotizacion o cliente..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </SearchContainer>
        <FilterButtons>
          <FilterButton active={filter === 'Todos'} onClick={() => setFilter('Todos')}>
            Todos ({quotes.length})
          </FilterButton>
          {FILTERS.slice(1).map(f => (
            <FilterButton key={f} active={filter === f} onClick={() => setFilter(f)}>
              {f} ({countByStatus(f)})
            </FilterButton>
          ))}
        </FilterButtons>
      </FilterSection>

      <ListSection>
        <SectionTitle>Lista de Cotizaciones ({filtered.length})</SectionTitle>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <Th>Numero</Th>
                <Th>Cliente</Th>
                <Th>Total</Th>
                <Th>Estado</Th>
                <Th>Fecha Emision</Th>
                <Th>Valida Hasta</Th>
                <Th>Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <SkeletonTableRows rows={8} cols={7} />
              ) : paginated.length > 0 ? (
                paginated.map(q => (
                  <tr key={q.id}>
                    <Td style={{ fontWeight: 600 }}>{q.numero_cotizacion || `COT-${q.id}`}</Td>
                    <Td>{q.cliente_nombre || 'N/A'}</Td>
                    <Td>
                      ${(q.monto_total || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </Td>
                    <Td>
                      <StatusBadge status={q.estado}>
                        {q.estado
                          ? q.estado.charAt(0).toUpperCase() + q.estado.slice(1)
                          : 'N/A'}
                      </StatusBadge>
                    </Td>
                    <Td>
                      {q.fecha_emision
                        ? new Date(q.fecha_emision).toLocaleDateString('es-MX')
                        : 'N/A'}
                    </Td>
                    <Td>
                      {q.fecha_validez
                        ? new Date(q.fecha_validez).toLocaleDateString('es-MX')
                        : 'N/A'}
                    </Td>
                    <Td>
                      <ActionButton onClick={() => navigate(`/cotizaciones/${q.id}`)}>
                        <MdVisibility />
                        Ver
                      </ActionButton>
                    </Td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7">
                    <EmptyState
                      type="receipts"
                      title={searchTerm || filter !== 'Todos' ? 'Sin resultados' : 'Sin cotizaciones aun'}
                      subtitle={
                        searchTerm || filter !== 'Todos'
                          ? 'Intenta con otro termino de busqueda o cambia el filtro.'
                          : 'Crea tu primera cotizacion para empezar.'
                      }
                      actionLabel={!searchTerm && filter === 'Todos' ? 'Nueva Cotizacion' : undefined}
                      onAction={!searchTerm && filter === 'Todos' ? () => navigate('/cotizaciones/nueva') : undefined}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </TableWrapper>

        {totalPages > 1 && (
          <Pagination>
            <PageButton onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>
              Anterior
            </PageButton>
            {getPageNumbers().map((page, i) =>
              page === '...' ? (
                <span key={`e-${i}`} style={{ padding: '0 8px' }}>...</span>
              ) : (
                <PageButton key={page} active={currentPage === page} onClick={() => setCurrentPage(page)}>
                  {page}
                </PageButton>
              )
            )}
            <PageButton onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}>
              Siguiente
            </PageButton>
          </Pagination>
        )}
      </ListSection>
    </QuotesContainer>
  );
};

export default Quotes;
